import { motion } from 'framer-motion';
import { LogIn, UserPlus, ArrowRight, Shield } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import { useState } from 'react';

export default function AcceptNominateScene({ onNext }) {
  const [choice, setChoice] = useState(null);

  return (
    <div data-testid="scene-accept-nominate" className="flex flex-col items-center justify-center h-full px-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full max-w-4xl"
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-medium bg-teal-600/10 text-teal-700 mb-4">
            <Shield size={14} /> Step 6
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-[#0A0A0A]">
            Accept or Nominate
          </h2>
          <p className="mt-3 font-body text-lg text-[#737373] flex items-center justify-center gap-2">
            User can login to the portal or nominate someone else
            <InfoSpot text="After OTP verification, the invited user has two options: Accept and login directly, or nominate another person by providing their EID and name — a new invite will be sent to the nominee." position="bottom" color="blue" />
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Accept & Login */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            whileHover={{ y: -4, boxShadow: '0 25px 50px -12px rgba(13,148,136,0.15)' }}
            onClick={() => setChoice('accept')}
            className={`bg-white rounded-2xl border-2 shadow-xl p-8 cursor-pointer transition-colors ${
              choice === 'accept' ? 'border-teal-600' : 'border-transparent'
            }`}
          >
            <div className="w-14 h-14 rounded-2xl bg-teal-600 flex items-center justify-center mb-6">
              <LogIn size={24} className="text-white" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-[#0A0A0A] mb-3">
              Accept & Login
            </h3>
            <p className="font-body text-sm text-[#737373] mb-6 leading-relaxed">
              Accept the invitation and proceed to login to the Shafafiya portal with your credentials.
            </p>

            {choice === 'accept' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                <div className="pt-4 border-t border-[#E5E7EB] space-y-3">
                  <div className="p-3 rounded-lg bg-[#FAFAFA]">
                    <p className="text-xs font-body text-[#737373] mb-1">Logged in as</p>
                    <p className="text-sm font-body font-medium text-[#0A0A0A]">Ahmed Al Rashid</p>
                    <p className="text-xs font-body text-[#737373]">Organization Owner</p>
                  </div>
                  <motion.button
                    data-testid="login-portal-button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onNext}
                    className="w-full py-3 rounded-xl bg-teal-600 text-white font-body font-bold text-sm flex items-center justify-center gap-2 cursor-pointer"
                  >
                    Enter Portal <ArrowRight size={16} />
                  </motion.button>
                </div>
              </motion.div>
            )}

            {!choice && (
              <div className="flex items-center gap-2 text-teal-700 font-body font-medium text-sm">
                Select this option <ArrowRight size={14} />
              </div>
            )}
          </motion.div>

          {/* Nominate Someone Else */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            whileHover={{ y: -4, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.1)' }}
            onClick={() => setChoice('nominate')}
            className={`bg-white rounded-2xl border-2 shadow-xl p-8 cursor-pointer transition-colors ${
              choice === 'nominate' ? 'border-[#0A0A0A]' : 'border-transparent'
            }`}
          >
            <div className="w-14 h-14 rounded-2xl bg-[#0A0A0A] flex items-center justify-center mb-6">
              <UserPlus size={24} className="text-white" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-[#0A0A0A] mb-3">
              Nominate Someone
            </h3>
            <p className="font-body text-sm text-[#737373] mb-6 leading-relaxed">
              Delegate access by nominating another person. Provide their EID and name to send them an invite.
            </p>

            {choice === 'nominate' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                <div className="pt-4 border-t border-[#E5E7EB] space-y-3">
                  <div>
                    <label className="text-xs font-body font-medium text-[#737373] mb-1 block">Nominee EID</label>
                    <div data-testid="nominee-eid-input" className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-[#FAFAFA] text-sm font-body text-[#0A0A0A]">
                      784-5566-7788991-4
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-body font-medium text-[#737373] mb-1 block">Nominee Name</label>
                    <div data-testid="nominee-name-input" className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-[#FAFAFA] text-sm font-body text-[#0A0A0A]">
                      Sara Al Jabri
                    </div>
                  </div>
                  <motion.button
                    data-testid="send-nomination-button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onNext}
                    className="w-full py-3 rounded-xl bg-[#0A0A0A] text-white font-body font-bold text-sm flex items-center justify-center gap-2 cursor-pointer"
                  >
                    Send Nomination <ArrowRight size={16} />
                  </motion.button>
                </div>
              </motion.div>
            )}

            {!choice && (
              <div className="flex items-center gap-2 text-[#0A0A0A] font-body font-medium text-sm">
                Select this option <ArrowRight size={14} />
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
